import type { IntlShape } from "@formatjs/intl";
import type { Dispatch } from "@reduxjs/toolkit";
import { Button, Form, Skeleton } from "antd";
import { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { actions as routerActions } from "redux-router5";
import ConnectedAppLayout from "../AppLayout.tsx";
import type { RootState } from "../store.ts";
import ModalFooter from "../../common/components/ModalFooter";
import { PageTitleContainer } from "../../common/components/PageTitle.tsx";
import { routeNames } from "../common/routes.ts";
import { servicesActions } from "../../features/services/servicesSlice";
import type { Materials, Services } from "../generated-axios-client/index.ts";
import { ServiceFields } from "./ServiceFields";

type NewServicePageProps = {
	dispatch: Dispatch;
	intl: IntlShape;
	initialValue?: Services | null;
	materialItems: Materials[];
};

export function NewServicePage({
	dispatch,
	intl,
	initialValue,
	materialItems,
}: NewServicePageProps) {
	const [form] = Form.useForm();

	const isEdit = !!initialValue?.id;

	const save = () => {
		form
			.validateFields()
			.then((values) => {
				const materialsPayload = (values.materials ?? []).map((id: string) => ({
					material: id,
				}));
				const payload = isEdit
					? { ...values, id: initialValue.id, materials: materialsPayload }
					: { ...values, materials: materialsPayload };
				dispatch(servicesActions.addOrUpdateService(payload));
			})
			.catch((errorInfo) => {
				console.error("Validation failed:", errorInfo);
			});
	};

	const initialValues = useMemo(
		() =>
			initialValue
				? {
						...initialValue,
						materials: initialValue.materials?.map((m) => m.material) ?? [],
					}
				: undefined,
		[initialValue],
	);

	useEffect(() => {
		if (initialValues) {
			form.setFieldsValue(initialValues);
		} else {
			form.resetFields();
		}
	}, [initialValues, form]);

	if (isEdit && !initialValue) {
		return <Skeleton active />;
	}

	const cancelDestination = isEdit
		? routeNames.SETTINGS_SERVICE_VIEW
		: routeNames.SETTINGS_SERVICE_LIST;

	const cancelParams = isEdit ? { id: initialValue?.id } : undefined;

	return (
		<ConnectedAppLayout>
			<PageTitleContainer
				title={intl.formatMessage({
					id: isEdit ? "service_edit_title" : "service_create_title",
				})}
				subtitle={
					isEdit
						? undefined
						: intl.formatMessage({ id: "service_create_subtitle" })
				}
			>
				<Form form={form} layout="vertical" initialValues={initialValues}>
					<ServiceFields intl={intl} materialItems={materialItems} />
				</Form>
				<ModalFooter>
					<Button
						onClick={() =>
							dispatch(
								routerActions.navigateTo(cancelDestination, cancelParams),
							)
						}
					>
						{intl.formatMessage({ id: "ticket_button_title_cancel" })}
					</Button>
					<Button type="primary" onClick={save}>
						{intl.formatMessage({ id: "ticket_button_title_create" })}
					</Button>
				</ModalFooter>
			</PageTitleContainer>
		</ConnectedAppLayout>
	);
}

export const ConnectedNewServicePage = connect((state: RootState) => ({
	intl: state.intl.datetimeIntl,
	initialValue: state.services.service,
	materialItems: state.materials.list.results ?? [],
}))(NewServicePage);

export default ConnectedNewServicePage;
